/**
 * Formatting utilities for token amounts and display
 * Uses BigInt for precise calculations (no floating point errors)
 */

/**
 * Convert raw token amount (bigint) to human-readable decimal string
 * @param amount - Raw token amount as bigint
 * @param decimals - Number of decimal places for the token
 * @returns Formatted string with proper decimal placement
 */
export function formatTokenAmount(amount: bigint, decimals: number): string {
  if (decimals === 0) {
    return amount.toString();
  }

  const divisor = BigInt(10 ** decimals);
  const intPart = amount / divisor;
  const fracPart = amount % divisor;

  if (fracPart === 0n) {
    return intPart.toString();
  }

  const fracStr = fracPart.toString().padStart(decimals, "0");
  // Remove trailing zeros
  const trimmedFrac = fracStr.replace(/0+$/, "");

  return `${intPart}.${trimmedFrac}`;
}

/**
 * Convert human-readable amount string to raw bigint
 * @param amount - Human-readable amount (e.g., "1.5")
 * @param decimals - Number of decimal places for the token
 * @returns Raw amount as bigint
 */
export function parseTokenAmount(amount: string, decimals: number): bigint {
  const num = parseFloat(amount);
  if (isNaN(num) || num < 0) {
    return 0n;
  }

  // Use precise calculation to avoid floating point errors
  const [intPart, fracPart = ""] = amount.split(".");
  const paddedFrac = fracPart.slice(0, decimals).padEnd(decimals, "0");

  return BigInt(intPart || "0") * BigInt(10 ** decimals) + BigInt(paddedFrac);
}

/**
 * Format a number with thousands separators
 * @param value - Number or string to format
 * @returns Formatted string with commas
 */
export function formatWithCommas(value: number | string): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0";

  return num.toLocaleString("en-US", {
    maximumFractionDigits: 9,
  });
}

/**
 * Shorten a Solana address for display
 * @param address - Full base58 address
 * @param chars - Number of characters to show on each end (default 4)
 * @returns Shortened address like "AbCd...WxYz"
 */
export function shortenAddress(address: string, chars: number = 4): string {
  if (address.length <= chars * 2 + 3) {
    return address;
  }
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Format SOL balance for display
 * @param lamports - Balance in lamports
 * @returns Formatted SOL amount
 */
export function formatSol(lamports: number): string {
  const sol = lamports / 1e9;
  if (sol < 0.001) {
    return "< 0.001";
  }
  return sol.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Estimate transaction fee in SOL
 * Currently returns a static estimate; could be enhanced to calculate dynamically
 * @returns Estimated fee in SOL
 */
export function estimateTransactionFee(): string {
  // Burn transaction is simple - typically costs ~0.000005 SOL
  return "~0.000005";
}

/**
 * Format token balance in compact form for display in token list rows.
 * NOT for use in transaction logic or confirmation modals — use formatTokenAmount there.
 *
 * Rules:
 * - >= 1B → "X.XXB"
 * - >= 1M → "X.XXM"
 * - >= 1K → "X.XXK"
 * - >= 1 → 2-4 decimals, max 8 chars
 * - < 1 → up to 6 significant digits
 *
 * @param amount - Raw token amount as bigint
 * @param decimals - Number of decimal places for the token
 * @returns Compact formatted string for display
 */
export function formatCompactBalance(amount: bigint, decimals: number): string {
  // Convert to number for easier formatting (precision loss is OK for display)
  const divisor = 10 ** decimals;
  const value = Number(amount) / divisor;

  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(2) + "B";
  }

  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(2) + "M";
  }

  if (value >= 1_000) {
    return (value / 1_000).toFixed(2) + "K";
  }

  if (value >= 1) {
    // For values >= 1, show 2-4 decimals, max 8 chars total
    const str = value.toFixed(4);
    // Trim trailing zeros but keep at least 2 decimals
    const parts = str.split(".");
    if (parts[1]) {
      const trimmed = parts[1].replace(/0+$/, "").padEnd(2, "0");
      const result = `${parts[0]}.${trimmed}`;
      // If still too long, reduce decimals
      if (result.length > 8) {
        return value.toFixed(2);
      }
      return result;
    }
    return str;
  }

  // For values < 1, show up to 6 significant digits
  if (value === 0) {
    return "0";
  }

  // Use toPrecision for small numbers
  const precise = value.toPrecision(6);
  // Convert back to avoid scientific notation for very small numbers
  const num = parseFloat(precise);
  if (num < 0.000001) {
    return "< 0.000001";
  }
  return num.toString();
}
